import React, { ReactElement, useState } from 'react';
import { Box } from '../Box';
import Typography from '../Typography';
import { TimeSelectorDayTooltip } from '../TimeSelectorDayTooltip/TimeSelectorDayTooltip';
import { DateBlock } from '../../types';
import { Badge } from '@mui/material';
import { Dayjs } from 'dayjs';
import { KeyboardArrowUpIcon } from '../Icons';

type TimeSelectorDayProps<TFeature> = {
    color?: string;
    text?: string;
    date?: string | ReactElement;
    height: string;
    selected: boolean;
    disabled?: boolean;
    items?: DateBlock<TFeature>[];
    onDateClick?: (date: DateBlock<TFeature>) => void;
    selectedValue?: string | number | Date | Dayjs | null | undefined;
};
/**
 * Each `TimeSelectorDay` represents a selectable time span. When used in temporal time selectors (such as in emissions drawers),
 * a badge is shown with the number of measurments per time span, and hovering over each timespan brings up a tooltip
 * that allows for exact date selection.
 *
 */
export function TimeSelectorDay<TFeature>({
    height,
    date,
    color,
    selected = false,
    disabled,
    items,
    onDateClick,
    selectedValue
}: TimeSelectorDayProps<TFeature>) {
    const [tooltipOpen, setTooltipOpen] = useState(false);

    return (
        <TimeSelectorDayTooltip
            items={items}
            onDateClick={onDateClick}
            selectedValue={selectedValue}
            selected={selected}
            open={tooltipOpen}
            onTooltipOpen={setTooltipOpen}
        >
            <Badge
                badgeContent={
                    <Box sx={{ display: 'flex', overflow: 'hidden' }}>
                        <>
                            {items?.length}
                            <KeyboardArrowUpIcon sx={{ fontSize: '0.6rem', ml: '-1px' }} />
                        </>
                    </Box>
                }
                invisible={!items?.length}
                sx={(theme) => ({
                    display: 'block',
                    '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        height: 16,
                        maxWidth: 22,
                        paddingRight: 0,
                        borderRadius: 1,
                        right: 10.5,
                        top: 8,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.secondary.main
                    }
                })}
            >
                <Box
                    sx={(theme) => ({
                        height: height,
                        background: color ?? theme.palette.lightGray.main,
                        padding: '6px',
                        border: '2px solid',
                        borderColor: selected ? theme.palette.primary.main : 'transparent',
                        cursor: !disabled && !selected ? 'pointer' : 'default',
                        transition: selected ? 'none' : 'box-shadow 500ms ease',
                        '&:hover':
                            !disabled && !selected
                                ? {
                                      boxShadow: ` 0 0 0 2px ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.action.hover}`
                                  }
                                : undefined,
                        borderRadius: 1.5,
                        stroke: color ?? theme.palette.lightGray.main,
                        opacity: disabled ? '0.5' : undefined,
                        alignItems: 'center',
                        display: 'flex'
                    })}
                >
                    {date && (
                        <Typography
                            sx={{
                                fontWeight: selected ? 700 : 400,
                                fontSize: '14px',
                                textTransform: 'uppercase',
                                lineHeight: '14px'
                            }}
                        >
                            {date}
                        </Typography>
                    )}
                </Box>
            </Badge>
        </TimeSelectorDayTooltip>
    );
}
